import { FormikErrors, FormikTouched } from "formik";
import React, { ForwardedRef, forwardRef, useState } from "react";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { TextFormField } from "./text_form_field";

import "./billing_details.css";
import { PickerFormField } from "./picker_form_field";
import { countries } from "../../offering/countries";
import { CheckoutStripeInput } from "@/offering/template_offering_checkout_stripe_base";
import { useBillingDetailsUpdate } from "@/core/splitio";
import { Checkbox } from "@/components";
import { Divider } from "./divider";

interface BillingDetailsInput {
  billingFullName?: string;
  billingPhoneNumber?: string;
  billingEmail?: string;
  companyName: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
}
interface BillingDetailsProps {
  askForTax: boolean;
  values: BillingDetailsInput;
  errors: FormikErrors<BillingDetailsInput>;
  touched: FormikTouched<BillingDetailsInput>;
  setFieldValue: (field: string, value: any) => void;
  errorFieldId?: keyof CheckoutStripeInput;
  onCheckContactDetailsSameAsBilling?: (value: boolean) => void;
  partnerId: string;
  setFieldTouched: (field: string) => void;
  onErrorTextRef?: (
    ref: HTMLElement | null,
    errorId: keyof BillingDetailsInput
  ) => void;
  onInputBorderRef?: (
    ref: HTMLElement | null,
    errorId: keyof BillingDetailsInput
  ) => void;
}

export const BillingDetails = forwardRef(
  (props: BillingDetailsProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      askForTax,
      values,
      touched,
      errors,
      setFieldValue,
      errorFieldId,
      onCheckContactDetailsSameAsBilling,
      partnerId,
      setFieldTouched,
      onErrorTextRef,
      onInputBorderRef,
    } = props;
    const { fbt } = useI18n();

    const isBillingDetailsUpdateFlagEnabled =
      useBillingDetailsUpdate(partnerId);

    const [areSameContactDetailsChecked, setAreSameContactDetailsChecked] =
      useState(false);

    const handleCheckSameContactDetails = (isChecked: boolean) => {
      if (!isChecked) {
        setFieldValue("billingFullName", "");
        setFieldValue("billingPhoneNumber", "");
        setFieldValue("billingEmail", "");
      } else {
        setFieldValue("billingFullName", undefined);
        setFieldValue("billingPhoneNumber", undefined);
        setFieldValue("billingEmail", undefined);
      }
      setAreSameContactDetailsChecked(isChecked);
      onCheckContactDetailsSameAsBilling?.(isChecked);
    };

    return (
      <div className="billing-details">
        {askForTax && isBillingDetailsUpdateFlagEnabled && (
          <div className="d-flex gap-3 mb-5">
            <Checkbox
              value={areSameContactDetailsChecked}
              onChange={handleCheckSameContactDetails}
            />
            <div>
              My contact details are the same as the billing contact details
            </div>
          </div>
        )}

        <h3 className="billing-details__title">{fbt("Billing details")}</h3>

        {!areSameContactDetailsChecked &&
          askForTax &&
          isBillingDetailsUpdateFlagEnabled && (
            <div>
              <div className="row">
                <div
                  className="col-12 col-lg-6 mb-4"
                  ref={errorFieldId === "billingFullName" ? ref : undefined}
                >
                  <TextFormField
                    name="billingFullName"
                    label={fbt("Full name")}
                    value={values.billingFullName || ""}
                    onChange={(billingFullName) =>
                      setFieldValue("billingFullName", billingFullName)
                    }
                    error={touched.billingFullName && errors.billingFullName}
                    onBlur={() => setFieldTouched("billingFullName")}
                    errorRef={(ref) => onErrorTextRef?.(ref, "billingFullName")}
                    inputRef={(ref) =>
                      onInputBorderRef?.(ref, "billingFullName")
                    }
                  />
                </div>
                <div
                  className="col-12 col-lg-6 mb-4"
                  ref={errorFieldId === "billingPhoneNumber" ? ref : undefined}
                >
                  <TextFormField
                    name="billingPhoneNumber"
                    label={fbt("Phone number")}
                    value={values.billingPhoneNumber || ""}
                    onChange={(billingPhoneNumber) =>
                      setFieldValue("billingPhoneNumber", billingPhoneNumber)
                    }
                    error={
                      touched.billingPhoneNumber && errors.billingPhoneNumber
                    }
                    onBlur={() => setFieldTouched("billingPhoneNumber")}
                    errorRef={(ref) =>
                      onErrorTextRef?.(ref, "billingPhoneNumber")
                    }
                    inputRef={(ref) =>
                      onInputBorderRef?.(ref, "billingPhoneNumber")
                    }
                  />
                </div>
                <div
                  className="col-12 col-lg-6 mb-4"
                  ref={errorFieldId === "billingEmail" ? ref : undefined}
                >
                  <TextFormField
                    name="billingEmail"
                    label={fbt("Email")}
                    value={values.billingEmail || ""}
                    onChange={(billingEmail) =>
                      setFieldValue("billingEmail", billingEmail)
                    }
                    error={touched.billingEmail && errors.billingEmail}
                    onBlur={() => setFieldTouched("billingEmail")}
                    errorRef={(ref) => onErrorTextRef?.(ref, "billingEmail")}
                    inputRef={(ref) => onInputBorderRef?.(ref, "billingEmail")}
                  />
                </div>
              </div>
              <Divider />
            </div>
          )}

        <div className="row">
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "billingStreetAddress" ? ref : undefined}
          >
            <TextFormField
              name="billingStreetAddress"
              label={fbt("Street address")}
              value={values.billingStreetAddress}
              onChange={(billingStreetAddress) =>
                setFieldValue("billingStreetAddress", billingStreetAddress)
              }
              error={
                touched.billingStreetAddress && errors.billingStreetAddress
              }
              onBlur={() => setFieldTouched("billingStreetAddress")}
              errorRef={(ref) => onErrorTextRef?.(ref, "billingStreetAddress")}
              inputRef={(ref) =>
                onInputBorderRef?.(ref, "billingStreetAddress")
              }
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "billingCity" ? ref : undefined}
          >
            <TextFormField
              name="billingCity"
              label={fbt("City / Town")}
              value={values.billingCity}
              onChange={(billingCity) =>
                setFieldValue("billingCity", billingCity)
              }
              error={touched.billingCity && errors.billingCity}
              onBlur={() => setFieldTouched("billingCity")}
              errorRef={(ref) => onErrorTextRef?.(ref, "billingCity")}
              inputRef={(ref) => onInputBorderRef?.(ref, "billingCity")}
            />
          </div>
          <div className="col-12 col-lg-6 mb-4">
            <TextFormField
              name="billingState"
              label={fbt("State / Province / Region (optional)")}
              value={values.billingState}
              onChange={(billingState) =>
                setFieldValue("billingState", billingState)
              }
              error={touched.billingState && errors.billingState}
              onBlur={() => setFieldTouched("billingState")}
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "billingPostalCode" ? ref : undefined}
          >
            <TextFormField
              name="billingPostalCode"
              label={fbt("Postal code")}
              value={values.billingPostalCode}
              onChange={(billingPostalCode) =>
                setFieldValue("billingPostalCode", billingPostalCode)
              }
              error={touched.billingPostalCode && errors.billingPostalCode}
              onBlur={() => setFieldTouched("billingPostalCode")}
              errorRef={(ref) => onErrorTextRef?.(ref, "billingPostalCode")}
              inputRef={(ref) => onInputBorderRef?.(ref, "billingPostalCode")}
            />
          </div>
          <div
            className="col-12 col-lg-6 mb-4"
            ref={errorFieldId === "billingCountry" ? ref : undefined}
          >
            <PickerFormField
              name="billingCountry"
              label={fbt("Country")}
              options={countries}
              value={values.billingCountry}
              onChange={(billingCountry) =>
                setFieldValue("billingCountry", billingCountry)
              }
              error={touched.billingCountry && errors.billingCountry}
              onBlur={() => setFieldTouched("billingCountry")}
              errorRef={(ref) => onErrorTextRef?.(ref, "billingCountry")}
              selectRef={(ref) => onInputBorderRef?.(ref, "billingCountry")}
            />
          </div>
          {askForTax && (
            <div
              className="col-12 col-lg-6 mb-4"
              ref={errorFieldId === "billingTax" ? ref : undefined}
            >
              <TextFormField
                name="billingTax"
                label={fbt("Tax / VAT / ID number")}
                value={values.billingTax}
                onChange={(billingTax) =>
                  setFieldValue("billingTax", billingTax)
                }
                error={touched.billingTax && errors.billingTax}
                onBlur={() => setFieldTouched("billingTax")}
                errorRef={(ref) => onErrorTextRef?.(ref, "billingTax")}
                inputRef={(ref) => onInputBorderRef?.(ref, "billingTax")}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);
